/**
 * @architecture-header 1
 * @layer app
 * @domain authentication
 * @responsibility Resolve host Supabase Auth or FastAPI organization-cookie sessions into one in-memory identity projection.
 * @state-owner host auth adapter or FastAPI cookie session; React context is an in-memory projection
 * @allowed-imports React; app environment
 * @forbidden localStorage; Zustand; role or permission ownership; API entity cache
 * @public-api app providers and authenticated routes; __setSessionProviderTestCommitSpy is test-only
 * @side-effects session bootstrap, organization logout and host auth-state subscription with cleanup
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 * @security-boundary host authentication adapter to shared React product
 * @data-classification user identity in memory; access token remains host-private
 * @trust-model host session proves identity only; backend remains authorization authority
 */

import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  useWorkspaceEnvironment,
  type WorkspaceAuthAdapter,
  type WorkspaceSession,
} from '../config/environment';

interface SessionContextValue {
  acceptOrganizationSession(session: OrganizationSessionIdentity): void;
  auth: WorkspaceAuthAdapter;
  error: Error | null;
  isLoading: boolean;
  session: WorkspaceSession | null;
  refresh(): Promise<void>;
  signOut(): Promise<void>;
}

export interface OrganizationSessionIdentity {
  csrf_token: string;
  expires_at: string;
  status: 'authenticated';
  user_id: string;
}

interface AnonymousOrganizationSession {
  status: 'anonymous';
}

type SessionSource = 'host' | 'organization' | null;

// resolveSession() awaits both the host adapter's getSession() and, for org-login-only
// identities, a real network call to /api/v1/auth/org-session -- neither carries an abort
// signal or timeout of its own. A bounded terminal path keeps a hung request from leaving the
// provider spinning in isLoading=true forever: exported so a caller (or a test) can override it.
export const DEFAULT_SESSION_BOOTSTRAP_TIMEOUT_MS = 10_000;

// Test-only commit instrumentation seam. Never read by production code paths and NOT part of
// SessionProvider's public props -- installed exclusively through this function, which
// SessionProvider.test.tsx uses to inject (and clear) a spy/counter for the single commit()
// primitive every session-installing/-clearing write in the provider routes through. This
// reaches no production consumer: SessionProvider's props type has no such field, and this
// module-level hook is a no-op unless a test explicitly calls it.
let testCommitSpy: (() => void) | undefined;

export function __setSessionProviderTestCommitSpy(spy: (() => void) | undefined): void {
  testCommitSpy = spy;
}

function projectOrganizationSession(session: OrganizationSessionIdentity): WorkspaceSession {
  return {
    displayName: null,
    email: '',
    portalAudience: 'student',
    userId: session.user_id,
    linkedProviders: [],
  };
}

function isMissingOrganizationSession(cause: unknown): boolean {
  return Boolean(cause && typeof cause === 'object' && 'status' in cause && cause.status === 401);
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({
  children,
  bootstrapTimeoutMs = DEFAULT_SESSION_BOOTSTRAP_TIMEOUT_MS,
}: PropsWithChildren<{ bootstrapTimeoutMs?: number }>) {
  const { apiClient, auth } = useWorkspaceEnvironment();
  const [session, setSession] = useState<WorkspaceSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const sourceRef = useRef<SessionSource>(null);
  const organizationCsrfRef = useRef<string | null>(null);
  const mountedRef = useRef(true);
  // The single monotonic supersession token. Both the initial bootstrap and refresh() call
  // beginResolution() to capture their own before starting an async resolveSession() call, and
  // every state-writing branch inside it is gated on this token still matching. Every real write
  // -- session, error, or loading -- goes through commit() below, which also advances this, so
  // "gen is still current" and "this attempt hasn't reached ANY terminal outcome yet" are the
  // same condition: no separate settled flag is needed once every settlement path, including a
  // rejection, commits through the one primitive.
  const activeGenRef = useRef(0);
  // Remembers a null host signal that arrived while the bootstrap resolution it belongs to was
  // still current: applied (as clearSession()) when that SAME resolution reaches its own
  // terminal outcome, unless something newer superseded it first. A null signal is real
  // information (session expiry, sign-out in another tab) the resolution's own outcome -- even a
  // successful one reading a now-stale cached session -- must never silently win over.
  const pendingHostNullRef = useRef<number | null>(null);

  // A write is only valid if the component is still mounted AND nothing newer has begun since
  // this token was captured. Checked immediately before every write, not once up front: an
  // arbitrary amount of real time (an await) separates each check from the write it guards.
  const isCurrentResolution = useCallback(
    (gen: number) => mountedRef.current && activeGenRef.current === gen,
    [],
  );

  // A synchronous external invalidation with no write of its own (signOut()'s up-front
  // pre-invalidation, and unmount): advances the generation only. The real writes that follow
  // (clearSession(), applyHostSession()) are separate, later commit() calls.
  const supersede = useCallback(() => {
    activeGenRef.current += 1;
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      supersede();
    };
  }, [supersede]);

  // THE single primitive every provider state write -- session, error, or loading -- routes
  // through, with no exceptions: not even beginResolution()'s own optimistic error-clear below.
  // Mounted-gated: a deferred signal settling after unmount (e.g. signOut()'s adapter call
  // resolving post-unmount) must never write, even if its own caller forgot to re-check. Then
  // advances the generation (a commit is atomic and immediate, never itself "in flight"),
  // performs the write, then notifies the test-only commit spy, then returns the generation this
  // commit landed on (or the unchanged current one if unmounted) -- beginResolution() below is
  // the one caller that needs that return value, to use as its own token. A superseded/late
  // resolution is already gated out before it ever reaches this call (see resolveSession's
  // isCurrentResolution checks), so advancing the generation unconditionally on every real
  // commit -- including a resolution's own timely one -- only ever protects work that hasn't
  // committed yet; it can never undo a write that already happened.
  const commit = useCallback((write: () => void): number => {
    if (!mountedRef.current) return activeGenRef.current;
    activeGenRef.current += 1;
    write();
    testCommitSpy?.();
    return activeGenRef.current;
  }, []);

  // Starts a new async resolution attempt (bootstrap or refresh()): advances the generation via
  // commit() -- superseding whatever was active before -- and, in that SAME commit, optimistically
  // clears any stale error left by a previous attempt. Reusing commit() (rather than bumping the
  // generation separately) is exactly what keeps this a single write through the one observable,
  // mounted-gated boundary: the generation this call lands on is commit()'s own return value, so
  // there is no second bump after establishing gen as current that would invalidate every
  // isCurrentResolution(gen) check for the attempt that just started.
  const beginResolution = useCallback(() => commit(() => setError(null)), [commit]);

  const clearSession = useCallback(() => {
    commit(() => {
      sourceRef.current = null;
      organizationCsrfRef.current = null;
      setSession(null);
      setError(null);
      setIsLoading(false);
    });
  }, [commit]);

  const applyHostSession = useCallback(
    (nextSession: WorkspaceSession | null) => {
      commit(() => {
        if (nextSession) {
          sourceRef.current = 'host';
          organizationCsrfRef.current = null;
          setSession(nextSession);
        } else if (sourceRef.current !== 'organization') {
          sourceRef.current = null;
          setSession(null);
        }
        setError(null);
        setIsLoading(false);
      });
    },
    [commit],
  );

  // Public acceptor: called by the real org-login flow (app/router/routes.tsx) and the
  // password-rotation success path (settings-student.queries.ts), entirely outside this
  // provider's own bootstrap or refresh(). A newer organization session accepted here must win
  // over any still in-flight (or already timed-out) resolution just as a host sign-in does.
  const acceptOrganizationSession = useCallback(
    (nextSession: OrganizationSessionIdentity) => {
      commit(() => {
        sourceRef.current = 'organization';
        organizationCsrfRef.current = nextSession.csrf_token;
        setSession(projectOrganizationSession(nextSession));
        setError(null);
        setIsLoading(false);
      });
    },
    [commit],
  );

  // Consumes a recorded pending null for gen, if any: applies it (clearSession() -- the null
  // wins over whatever this resolution would otherwise have committed, including a successful
  // but now-stale-looking result) and reports true so the caller skips its own would-be commit.
  // Returns false when there is nothing pending for this exact gen.
  const consumePendingHostNull = useCallback(
    (gen: number) => {
      if (pendingHostNullRef.current !== gen) return false;
      pendingHostNullRef.current = null;
      clearSession();
      return true;
    },
    [clearSession],
  );

  // gen is the token captured by beginResolution() when THIS specific resolution attempt
  // started (bootstrap or refresh) -- consulted immediately before every state-writing branch,
  // since a newer resolution or external event can supersede this one while an await is pending.
  const resolveSession = useCallback(
    async (gen: number) => {
      const hostSession = await auth.getSession();
      if (!isCurrentResolution(gen)) return;
      if (consumePendingHostNull(gen)) return;
      if (hostSession) {
        applyHostSession(hostSession);
        return;
      }
      try {
        const organization = await apiClient.request<
          OrganizationSessionIdentity | AnonymousOrganizationSession
        >('/api/v1/auth/org-session');
        if (!isCurrentResolution(gen)) return;
        // A recorded pendingHostNull is deliberately NOT consulted here: it is a signal about
        // the HOST auth mechanism specifically (a stale-cached getSession() result, or a real
        // sign-out/expiry event on that channel). An org-login session is a wholly separate
        // authentication mechanism (a cookie-based credential) that a null host signal carries
        // no information about at all, so a successful organization-session result must stand.
        if (organization.status === 'authenticated') {
          acceptOrganizationSession(organization);
          return;
        }
        clearSession();
      } catch (cause) {
        if (!isMissingOrganizationSession(cause)) throw cause;
        if (!isCurrentResolution(gen)) return;
        if (consumePendingHostNull(gen)) return;
        clearSession();
      }
    },
    [
      acceptOrganizationSession,
      apiClient,
      applyHostSession,
      auth,
      clearSession,
      consumePendingHostNull,
      isCurrentResolution,
    ],
  );

  const refresh = useCallback(async () => {
    const gen = beginResolution();
    try {
      await resolveSession(gen);
    } catch (cause) {
      if (!isCurrentResolution(gen)) return;
      if (consumePendingHostNull(gen)) return;
      const resolvedError =
        cause instanceof Error ? cause : new Error('Unable to load the session');
      // Two separate commits, not a redundancy: showing the error and clearing whatever session
      // existed are two distinct facts becoming true, each real instrumentation should see. No
      // further "just set isLoading" step is needed after this -- clearSession() below owns it.
      commit(() => setError(resolvedError));
      clearSession();
    }
    // No finally: resolveSession() either committed via applyHostSession/
    // acceptOrganizationSession/clearSession, returned early because something else already
    // superseded and committed on its own, or the catch above just committed. Every path already
    // owns isLoading by the time this function returns; a trailing "just in case" commit here
    // would only ever fire when nothing legitimately needs it -- and reaching it while
    // NOT superseded, after a real commit already advanced the generation, is provably
    // unreachable, exactly the finding this rework closes.
  }, [
    beginResolution,
    clearSession,
    commit,
    consumePendingHostNull,
    isCurrentResolution,
    resolveSession,
  ]);

  const signOut = useCallback(async () => {
    // A signed-out state must win unconditionally over any still in-flight resolution (bootstrap
    // or refresh) -- invalidated synchronously, before the network round trip below, so a late
    // settlement can never resurrect the session this call is signing out of, even while that
    // request is still pending. The actual clear happens below via clearSession()/
    // applyHostSession(null), each its own real commit -- mounted-gated by commit() itself, so
    // even if this adapter call resolves after unmount, that write is a no-op.
    supersede();
    if (sourceRef.current === 'organization') {
      const csrfToken = organizationCsrfRef.current;
      if (!csrfToken) throw new Error('Organization session is missing its CSRF proof');
      await apiClient.request('/api/v1/auth/org-logout', {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrfToken },
      });
      clearSession();
      return;
    }
    await auth.signOut();
    applyHostSession(null);
  }, [apiClient, applyHostSession, auth, clearSession, supersede]);

  useEffect(() => {
    const gen = beginResolution();

    // Bounded terminal path: if resolveSession() itself neither resolves nor rejects within
    // this bound (a hung fetch with no abort signal), treat it the same as "no session found"
    // instead of leaving the provider in a silent infinite spinner. The timeout is itself a
    // resolution-scoped commit -- gated by the exact same token check as every other branch of
    // this resolution, so it never fires for an attempt something newer has already superseded,
    // and firing it (via clearSession()) supersedes the attempt in turn: the original
    // resolveSession() call is never cancelled, so if its own request later settles, it must not
    // be able to overwrite whatever state applies from here on.
    const timeoutId = setTimeout(() => {
      if (!isCurrentResolution(gen)) return;
      if (consumePendingHostNull(gen)) return;
      clearSession();
    }, bootstrapTimeoutMs);

    void resolveSession(gen)
      .catch((cause: unknown) => {
        if (!isCurrentResolution(gen)) return;
        // A null host signal recorded while this resolution was still in flight wins over its
        // own rejection: we already have a real, later signal that the caller isn't
        // host-authenticated, which is strictly more information than a failed org-session
        // network check tells us -- "signed out" is the more correct terminal state than
        // showing an error the null already superseded.
        if (consumePendingHostNull(gen)) return;
        const resolvedError =
          cause instanceof Error ? cause : new Error('Unable to load the session');
        // isLoading must be set in this SAME commit, not left to a separate write below: every
        // branch that reaches this point either already returned (something else's commit
        // already owns isLoading) or is about to commit right here -- there is no remaining
        // path where a later, separate "just set isLoading" step is still needed. See the
        // finally block below for why it does not also attempt this.
        // isLoading must be set in this SAME commit, not left to a separate write below: every
        // branch that reaches this point either already returned (something else's commit
        // already owns isLoading) or is about to commit right here -- there is no remaining
        // path where a later, separate "just set isLoading" step is still needed. See the
        // finally block below for why it does not also attempt this.
        commit(() => {
          setError(resolvedError);
          setIsLoading(false);
        });
      })
      .finally(() => {
        // No commit here: every path through the .catch() above either already returned because
        // something else (a supersession, or consumePendingHostNull) already committed on its
        // own, or just committed error+isLoading itself right above. A resolveSession() that
        // resolves without throwing is the same story -- it either committed via
        // applyHostSession/acceptOrganizationSession/clearSession, or returned early because it
        // was superseded, in which case whatever superseded it already owns isLoading. Only
        // clearTimeout() is genuinely unconditional here.
        clearTimeout(timeoutId);
      });

    const unsubscribe = auth.subscribe((nextSession) => {
      if (!mountedRef.current) return;
      // A null host session from the host auth adapter's own initial change event (Supabase's
      // onAuthStateChange fires this from local storage, no network round trip) can resolve
      // well before resolveSession()'s organization-session cookie check above -- which is a
      // real network call and is still authoritative for org-login-only identities (no host
      // session ever exists for them). Trusting this early null immediately would flip isLoading
      // to false, and WorkspaceShell would redirect to /login, before that slower check has had
      // its say. While this resolution is still current (i.e. it hasn't reached any terminal
      // outcome or been superseded yet), remember the null instead of dropping it -- it is
      // applied the moment this SAME resolution settles (see consumePendingHostNull above),
      // taking precedence over whatever that resolution's own outcome would otherwise have been.
      if (!nextSession && isCurrentResolution(gen)) {
        pendingHostNullRef.current = gen;
        return;
      }
      applyHostSession(nextSession);
    });
    return () => {
      clearTimeout(timeoutId);
      unsubscribe();
    };
  }, [
    applyHostSession,
    auth,
    beginResolution,
    bootstrapTimeoutMs,
    clearSession,
    commit,
    consumePendingHostNull,
    isCurrentResolution,
    resolveSession,
  ]);

  const value = useMemo(
    () => ({ acceptOrganizationSession, auth, error, isLoading, refresh, session, signOut }),
    [acceptOrganizationSession, auth, error, isLoading, refresh, session, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useWorkspaceSession(): SessionContextValue {
  const value = useOptionalWorkspaceSession();
  if (!value) throw new Error('SessionProvider is missing');
  return value;
}

export function useOptionalWorkspaceSession(): SessionContextValue | null {
  return useContext(SessionContext);
}
